import {stringify} from 'query-string';
import {fetchUtils, DataProvider, CreateResult} from 'ra-core';

const encodeParams = params => {
  let hasFiles = false;
  const formData = new FormData();
  for (const [key, value] of Object.entries(params.data)) {
    // console.log(key, typeof value, value);
    const valueAsAny = value as any;

    if (valueAsAny?.rawFile) {
      hasFiles = true;
      formData.append(key, valueAsAny.rawFile);
    } else if (Array.isArray(valueAsAny) && valueAsAny.length) {
      const oldFiles: any[] = [];
      // eslint-disable-next-line no-loop-func
      valueAsAny.forEach(item => {
        if (item.rawFile) {
          hasFiles = true;
          formData.append(key, item.rawFile);
        } else {
          oldFiles.push(item);
          //formData.append(key, JSON.stringify(item));
        }
      });
      formData.append(key, JSON.stringify(oldFiles));
    } else {
      formData.append(key, JSON.stringify(value));
    }
  }
  return hasFiles ? formData : JSON.stringify(params.data);
};

/**
 * Maps react-admin queries to a simple REST API
 *
 * This REST dialect is similar to the one of FakeRest
 *
 * @see https://github.com/marmelab/FakeRest
 *
 * @example
 *
 * getList     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * getOne      => GET http://my.api.url/posts/123
 * getMany     => GET http://my.api.url/posts?filter={id:[123,456,789]}
 * update      => PUT http://my.api.url/posts/123
 * create      => POST http://my.api.url/posts
 * delete      => DELETE http://my.api.url/posts/123
 *
 * @example
 *
 * import * as React from "react";
 * import { Admin, Resource } from 'react-admin';
 * import simpleRestProvider from 'ra-data-simple-rest';
 *
 * import { PostList } from './posts';
 *
 * const App = () => (
 *     <Admin dataProvider={simpleRestProvider('http://path.to.my.api/')}>
 *         <Resource name="posts" list={PostList} />
 *     </Admin>
 * );
 *
 * export default App;
 */
export const simpleRestProvider = (
  apiUrl: string,
  httpClient = fetchUtils.fetchJson,
  countHeader: string = 'Content-Range',
): DataProvider => ({
  getList: (resource, params) => {
    const {page, perPage} = params.pagination;
    const {field, order} = params.sort;

    const rangeStart = (page - 1) * perPage;
    const rangeEnd = page * perPage - 1;

    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([rangeStart, rangeEnd]),
      filter: JSON.stringify(params.filter),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    const options =
      countHeader === 'Content-Range'
        ? {
            // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
            headers: new Headers({
              Range: `${resource}=${rangeStart}-${rangeEnd}`,
            }),
          }
        : {};

    return httpClient(url, options).then(({headers, json}) => {
      if (!headers.has(countHeader)) {
        throw new Error(
          `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`,
        );
      }
      return {
        data: json,
        total:
          countHeader === 'Content-Range'
            ? parseInt(headers?.get('content-range')?.split('/').pop() ?? '', 10)
            : parseInt(headers.get(countHeader.toLowerCase()) ?? ''),
      };
    });
  },

  getOne: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`).then(({json}) => ({
      data: json,
    })),

  getMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({id: params.ids}),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({json}) => ({data: json}));
  },

  getManyReference: (resource, params) => {
    const {page, perPage} = params.pagination;
    const {field, order} = params.sort;

    const rangeStart = (page - 1) * perPage;
    const rangeEnd = page * perPage - 1;

    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    const options =
      countHeader === 'Content-Range'
        ? {
            // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
            headers: new Headers({
              Range: `${resource}=${rangeStart}-${rangeEnd}`,
            }),
          }
        : {};

    return httpClient(url, options).then(({headers, json}) => {
      if (!headers.has(countHeader)) {
        throw new Error(
          `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`,
        );
      }
      return {
        data: json,
        total:
          countHeader === 'Content-Range'
            ? parseInt(headers?.get('content-range')?.split('/').pop() ?? '', 10)
            : parseInt(headers.get(countHeader.toLowerCase()) ?? ''),
      };
    });
  },

  update: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PUT',
      body: encodeParams(params),
    }).then(({json}) => ({data: json}));
  },
  // simple-rest doesn't handle provide an updateMany route, so we fallback to calling update n times instead
  updateMany: (resource, params) =>
    Promise.all(
      params.ids.map(id =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'PUT',
          body: encodeParams(params),
        }),
      ),
    ).then(responses => ({data: responses.map(({json}) => json.id)})),

  create: (resource, params): Promise<CreateResult<any>> => {
    const formData = new FormData();
    for (const [key, value] of Object.entries(params.data)) {
      // console.log(`${key}: ${value} (${typeof value})`);
      formData.append(key, (value as any)?.rawFile ?? JSON.stringify(value));
    }
    return httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: encodeParams(params), //JSON.stringify(params.data),
    }).then(({json}) => ({
      data: {...params.data, id: json.id},
    }));
  },
  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({json}) => ({data: json})),

  // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
  deleteMany: (resource, params) =>
    Promise.all(
      params.ids.map(id =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'DELETE',
        }),
      ),
    ).then(responses => ({data: responses.map(({json}) => json.id)})),
});
export default simpleRestProvider;
